import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { ServiceLeadForm } from '@/components/ServiceLeadForm';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Home, ArrowRight, Check, Star, TrendingUp, Shield, Phone, Clock, Target, Handshake, Eye } from 'lucide-react';

// Images - Belle villa suisse moderne
const HERO_IMAGE = "https://images.pexels.com/photos/36025719/pexels-photo-36025719.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CONTENT_IMAGE_1 = "https://images.unsplash.com/photo-1702422576008-7445171a3719?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBzd2lzcyUyMHZpbGxhJTIwbW9kZXJuJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3NDEyOTA0N3ww&ixlib=rb-4.1.0&q=85";
const CONTENT_IMAGE_2 = "https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export const ImmoPage = () => {
  const questions = [
    { id: 'type_bien', label: 'Type de bien ?', type: 'select', options: ['Appartement', 'Maison', 'Chalet', 'Villa', 'Terrain', 'Local commercial'] },
    { id: 'localisation', label: 'Localisation ?', type: 'text', placeholder: 'Ex: Lausanne, Pully...' },
    { id: 'surface', label: 'Surface ?', type: 'text', placeholder: 'Ex: 120 m²' },
    { id: 'deja_vente', label: 'Bien déjà en vente ?', type: 'select', options: ['Oui, mais ça stagne', 'Oui, avec une agence', 'Non, je veux commencer', 'Je réfléchis'] },
    { id: 'delai', label: 'Délai souhaité ?', type: 'select', options: ['Urgent', '1-3 mois', '3-6 mois', 'Pas de rush'] },
    { id: 'prix_estime', label: 'Prix estimé ou attendu ?', type: 'text', placeholder: 'Ex: 850\'000 CHF (optionnel)' }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="immo-page">
      <Header />
      
      {/* ============================================
          PILIER 1: ATTENTION - Hero avec villa suisse
          ============================================ */}
      <section className="hero-section relative min-h-[70vh] sm:min-h-[80vh] flex items-center">
        <img 
          src={HERO_IMAGE} 
          alt="Villa de luxe avec piscine à débordement et vue sur les Alpes suisses" 
          className="hero-image-bg"
        />
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8 py-10 sm:py-16 md:py-20">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
              <Home className="h-7 w-7 sm:h-8 sm:w-8 md:h-10 md:w-10 text-white" />
              <Badge className="bg-white/20 text-white border-white/30 backdrop-blur-sm text-xs sm:text-sm">
                Homely Immo
              </Badge>
            </div>
            
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6 text-white leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              Vendez votre bien au meilleur prix, sans stress
            </h1>
            
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 mb-4 sm:mb-6 max-w-2xl">
              Estimation précise, mise en valeur professionnelle, visites qualifiées et accompagnement 
              jusqu'à la signature. Nous maximisons la valeur de votre bien immobilier en Suisse romande.
            </p>

            {/* Métriques clés */}
            <div className="flex flex-wrap gap-2 sm:gap-3 md:gap-4 mb-5 sm:mb-8">
              <div className="flex items-center gap-1.5 sm:gap-2 bg-white/10 backdrop-blur-sm px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full">
                <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />
                <span className="text-white text-xs sm:text-sm font-semibold">+8% vs prix marché</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 bg-white/10 backdrop-blur-sm px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full">
                <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400" />
                <span className="text-white text-xs sm:text-sm">45 jours délai moyen</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 bg-white/10 backdrop-blur-sm px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full">
                <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400" />
                <span className="text-white text-xs sm:text-sm">Accompagnement complet</span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button 
                size="lg" 
                className="btn-premium h-12 sm:h-14 px-6 sm:px-10 text-sm sm:text-base rounded-full w-full sm:w-auto"
                onClick={() => document.getElementById('form').scrollIntoView({ behavior: 'smooth' })}
                data-testid="immo-cta-primary"
              >
                Estimer mon bien gratuitement
                <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          PILIER 2: ÉMOTION - Les frustrations
          ============================================ */}
      <section className="py-12 sm:py-16 md:py-20">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12 items-center">
            <div>
              <Badge className="mb-3 sm:mb-4 bg-orange-100 text-orange-700 text-xs sm:text-sm">Le constat</Badge>
              
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Vendre seul ou avec une agence classique : les pièges à éviter
              </h2>
              
              <p className="text-sm sm:text-base md:text-lg text-muted-foreground mb-4 sm:mb-6">
                Vous pensez économiser en vendant seul ? Ou vous avez déjà signé avec une agence 
                qui ne donne pas de nouvelles ? Beaucoup de propriétaires se retrouvent coincés 
                avec un bien qui stagne sur le marché... et finissent par brader.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  { icon: '📉', title: 'Prix mal calibré', desc: 'Trop haut = pas de visite. Trop bas = vous perdez des milliers de francs.' },
                  { icon: '📷', title: 'Photos amateur', desc: '90% des acheteurs commencent leur recherche en ligne. Une mauvaise photo = aucun clic.' },
                  { icon: '🏚️', title: 'Bien non préparé', desc: 'Les acheteurs voient les défauts avant le potentiel. Résultat : offres basses.' },
                  { icon: '⏰', title: 'Visites non qualifiées', desc: 'Des curieux, des indécis... mais pas d\'acheteurs sérieux.' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4 p-4 bg-secondary/30 rounded-lg">
                    <span className="text-2xl">{item.icon}</span>
                    <div>
                      <p className="font-semibold">{item.title}</p>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-lg font-semibold" style={{ color: 'var(--sage-dark)' }}>
                Et si vous aviez un partenaire qui maximise réellement la valeur de votre bien ?
              </p>
            </div>

            <div className="relative">
              <img 
                src={CONTENT_IMAGE_1} 
                alt="Maison suisse de luxe sur colline verdoyante" 
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-lg max-w-xs">
                <p className="font-bold text-3xl" style={{ color: 'var(--sage)' }}>+8%</p>
                <p className="text-sm text-muted-foreground">au-dessus du prix marché en moyenne</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          PILIER 3: MÉMOIRE - Notre méthode
          ============================================ */}
      <section className="py-20 gradient-premium">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4" style={{ backgroundColor: 'var(--sage)', color: 'white' }}>Notre méthode</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              5 étapes pour vendre au meilleur prix
            </h2>
            <div className="section-divider"></div>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Une approche structurée, des outils professionnels et un accompagnement humain 
              pour chaque étape de votre vente.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-16">
            {[
              { icon: Target, step: '01', title: 'Estimation', desc: 'Analyse de marché précise basée sur les transactions réelles' },
              { icon: Eye, step: '02', title: 'Valorisation', desc: 'Home staging, photos professionnelles, vidéo drone' },
              { icon: '📝', step: '03', title: 'Annonce', desc: 'Rédaction optimisée, diffusion multi-plateformes' },
              { icon: Handshake, step: '04', title: 'Visites', desc: 'Qualification des acheteurs, visites ciblées' },
              { icon: '✍️', step: '05', title: 'Négociation', desc: 'Accompagnement jusqu\'à la signature chez le notaire' }
            ].map((item, idx) => (
              <div key={idx} className="card-premium p-6 text-center">
                <div className="text-4xl font-bold mb-3" style={{ color: 'var(--sage)' }}>{item.step}</div>
                {typeof item.icon === 'string' ? (
                  <span className="text-3xl mb-3 block">{item.icon}</span>
                ) : (
                  <item.icon className="h-8 w-8 mx-auto mb-3" style={{ color: 'var(--sage)' }} />
                )}
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Image + Liste avantages */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <img 
              src={CONTENT_IMAGE_2} 
              alt="Architecture moderne de villa contemporaine" 
              className="rounded-2xl shadow-xl w-full"
            />
            <div>
              <h3 className="text-2xl font-bold mb-6">Ce qui est inclus dans notre accompagnement :</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  'Estimation de marché détaillée',
                  'Conseils de valorisation',
                  'Photos professionnelles HDR',
                  'Vidéo et visite virtuelle 360°',
                  'Rédaction annonce optimisée',
                  'Diffusion multi-plateformes',
                  'Qualification des acheteurs',
                  'Organisation des visites',
                  'Négociation experte',
                  'Accompagnement notaire',
                  'Suivi administratif',
                  'Reporting régulier'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          PILIER 4: DÉCISION - Preuves et chiffres
          ============================================ */}
      <section className="py-20">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          
          {/* Stats bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 p-8 rounded-2xl mb-16" style={{ backgroundColor: 'var(--sage)' }}>
            {[
              { value: '85+', label: 'Biens vendus' },
              { value: '45 jours', label: 'Délai moyen de vente' },
              { value: '+8%', label: 'vs prix marché' },
              { value: '100%', label: 'Accompagnement' }
            ].map((stat, idx) => (
              <div key={idx} className="text-center text-white">
                <p className="text-4xl font-bold mb-1">{stat.value}</p>
                <p className="text-sm text-white/80">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Témoignages */}
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Ils ont vendu avec Homely Immo
            </h2>
            <div className="section-divider"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              { 
                name: 'Claude & Anne M.', 
                city: 'Pully', 
                bien: 'Villa 6 pièces',
                text: 'Après 6 mois sans résultat avec une agence classique, Homely a vendu notre villa en 5 semaines. Les photos et la mise en valeur ont fait toute la différence.', 
                result: 'Vendu +12% vs estimation initiale'
              },
              { 
                name: 'Jean-Luc B.', 
                city: 'Montreux', 
                bien: 'Appartement vue lac',
                text: 'Je pensais vendre seul pour économiser. Homely m\'a convaincu avec leur approche. Résultat : vendu en 3 semaines, 50\'000 CHF de plus que mon prix initial.', 
                result: 'Vendu en 21 jours'
              },
              { 
                name: 'Succession Müller', 
                city: 'Lausanne', 
                bien: 'Immeuble locatif',
                text: 'Vente complexe suite à un héritage. Homely a géré tous les aspects, des locataires aux notaires. Professionnalisme exemplaire.', 
                result: 'Vente conclue en 60 jours'
              }
            ].map((review, idx) => (
              <div key={idx} className="card-premium p-6">
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-4 italic">"{review.text}"</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-[var(--sage)] flex items-center justify-center text-white font-bold">
                      {review.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{review.name}</p>
                      <p className="text-xs text-muted-foreground">{review.bien} • {review.city}</p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm font-semibold" style={{ color: 'var(--sage)' }}>{review.result}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          PILIER 5: ACTION - Formulaire
          ============================================ */}
      <section className="py-20" style={{ backgroundColor: 'var(--anthracite)' }} id="form">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="text-white">
              <Badge className="mb-4 bg-[var(--sage)]">Estimation gratuite</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Découvrez la vraie valeur de votre bien
              </h2>
              <p className="text-white/70 mb-8 text-lg">
                Recevez une estimation de marché précise basée sur les transactions réelles 
                de votre quartier. Sans engagement, 100% gratuit.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  'Estimation basée sur les ventes récentes',
                  'Analyse des points forts et axes d\'amélioration',
                  'Conseils de valorisation personnalisés',
                  'Aucun engagement, totalement gratuit'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-[var(--sage)] flex items-center justify-center">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-white/90">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              <ServiceLeadForm service="vendre" pole="immo" questions={questions} />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section pour SEO */}
      <section className="py-16 border-t">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold mb-8 text-center">Questions fréquentes</h2>
          <div className="max-w-3xl mx-auto space-y-6">
            {[
              { q: 'Combien coûte votre accompagnement ?', a: 'Notre commission est de 2.5% à 3% du prix de vente selon le type de bien et les services inclus. Pas de frais si le bien n\'est pas vendu.' },
              { q: 'En combien de temps puis-je vendre ?', a: 'En moyenne, nos biens se vendent en 45 jours. Certains biens bien positionnés trouvent acquéreur en moins de 2 semaines.' },
              { q: 'Quelle est votre zone d\'intervention ?', a: 'Nous intervenons dans toute la Suisse romande : Vaud, Genève, Valais, Fribourg et Neuchâtel.' },
              { q: 'Comment fonctionne l\'estimation ?', a: 'Nous analysons les transactions récentes dans votre quartier, l\'état de votre bien et les tendances du marché pour vous donner une fourchette de prix réaliste.' }
            ].map((faq, idx) => (
              <div key={idx} className="p-6 bg-secondary/20 rounded-lg">
                <h3 className="font-semibold mb-2">{faq.q}</h3>
                <p className="text-muted-foreground">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
